import { useLayoutEffect, useRef } from 'react';
import { ANIMATION_DURATIONS } from '../../../constants/animations';
import { isSdkStatus, STATUS_SDK } from '../../../constants/status';
import { useDeviceDetection } from './useDeviceDetection';

/**
 * Hook para gerenciar animação de altura de um container
 *
 * Anima suavemente quando o conteúdo muda de altura, mas evita animações redundantes
 * quando apenas o texto do GlobalProgress muda (altura não muda).
 *
 * Técnica utilizada:
 * 1. Captura altura atual (getBoundingClientRect)
 * 2. Permite conteúdo definir nova altura (height: auto)
 * 3. Captura nova altura
 * 4. Volta para altura antiga sem transição
 * 5. Anima para nova altura com transição CSS
 *
 * Otimizações:
 * - Usa dataset.showingGlobalProgress para detectar mudanças de tipo de conteúdo
 * - Se estava e continua mostrando GlobalProgress, não anima (apenas texto mudou)
 * - Usa getBoundingClientRect() para medições precisas (sub-pixel)
 * - Tolerância de 1px para evitar animações desnecessárias
 *
 * Tratamento de Resize:
 * - Adiciona listeners para eventos 'resize' e 'orientationchange'
 * - Implementa debounce de 150ms para otimizar performance durante resize
 * - Recalcula e reanima a altura do wrapper quando a janela muda de tamanho
 * - Garante que mudanças de orientação do dispositivo sejam tratadas adequadamente
 * - Remove listeners e cancela timeouts pendentes no cleanup
 * - Código refatorado com funções auxiliares para reduzir aninhamento (Sonar compliance)
 *
 * @param {string} displayStatusType - Tipo do status atual (null, "loading", "success", "error")
 * @param {string} displayLocationPathname - Pathname da location atual
 * @param {boolean} globalProgressBusy - Se o GlobalProgress está ocupado
 * @param {boolean} isShowingGlobalProgress - Se está exibindo o GlobalProgress (evita animar ao trocar apenas o texto do status)
 * @param {Object|null} status - Status prop original para verificar se é SDK
 * @returns {Object} Refs para o container
 * @returns {React.RefObject} returns.wrapperRef - Ref para o elemento wrapper (com animação de altura)
 * @returns {React.RefObject} returns.contentRef - Ref para o elemento de conteúdo (para medir altura)
 *
 * @example
 * const { wrapperRef, contentRef } = useHeightAnimation(
 *   displayStatus?.type,
 *   displayLocation.pathname,
 *   globalProgressBusy,
 *   isShowingGlobalProgress,
 *   status
 * );
 *
 * return (
 *   <div ref={wrapperRef} className="wrapper">
 *     <div ref={contentRef}>{content}</div>
 *   </div>
 * );
 */
export const useHeightAnimation = (
  displayStatusType,
  displayLocationPathname,
  globalProgressBusy,
  isShowingGlobalProgress,
  status = null,
  headerContentRef = null,
) => {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const animationTimeoutRef = useRef(null);
  const lastAnimationStateRef = useRef(null);
  const previousContentHeightRef = useRef(null); // Armazena altura do conteúdo anterior
  const resizeTimeoutRef = useRef(null); // Para debounce do resize
  const isDesktop = useDeviceDetection();

  // Quando um modal está aberto, o ModalRenderer aplica transform: scale() no
  // wrapper. Medir a altura nesse estado (via getBoundingClientRect) retorna
  // o valor JÁ escalado, e fixar essa altura encolhe o card permanentemente
  // (520 → 468 = ×0.9). Enquanto o wrapper estiver escalado, pulamos a
  // medição/animação de altura — a altura volta ao normal quando o modal
  // fecha e o transform é removido.
  const isWrapperScaled = () => {
    const t = wrapperRef.current?.style.transform || '';
    return t.includes('scale(');
  };

  useLayoutEffect(() => {
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }

    // Modal aberto (wrapper escalado): não remedir/animar a altura — a
    // medição viria escalada e encolheria o card. Ver isWrapperScaled.
    if (isWrapperScaled()) {
      return;
    }

    // Se o status for SDK (prop ou displayStatus), não anima altura (altura é gerenciada pelo DynamicWrapper)
    if (isSdkStatus(status) || displayStatusType === STATUS_SDK) {
      // Marca que SDK foi exibido, para que a próxima transição anime corretamente
      const sdkState = `sdk-${displayLocationPathname}-${globalProgressBusy}`;
      if (lastAnimationStateRef.current !== sdkState) {
        lastAnimationStateRef.current = sdkState;
      }
      return;
    }

    // Cria um identificador único para este estado
    const currentState = `${displayStatusType}-${displayLocationPathname}-${globalProgressBusy}`;

    // Se já animamos para este estado, não faz nada (Strict Mode proteção)
    // if (lastAnimationStateRef.current === currentState) {
    //   return;
    // }

    // Cancela qualquer animação pendente (Strict Mode pode executar 2x)
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    // Na montagem inicial (lastAnimationStateRef é null), não anima e deixa wrapper em height:auto
    // A altura será fixada somente quando a primeira transição acontecer
    // Usamos lastAnimationStateRef ao invés de isInitialMountRef para ser resiliente ao Strict Mode
    if (lastAnimationStateRef.current === null) {
      wrapperRef.current.dataset.showingGlobalProgress = isShowingGlobalProgress
        ? 'true'
        : 'false';

      // Aguarda conteúdo carregar (API, etc) e armazena altura inicial para primeira transição
      animationTimeoutRef.current = setTimeout(() => {
        if (!contentRef.current) return;

        const initialHeight = contentRef.current.getBoundingClientRect().height;
        previousContentHeightRef.current = initialHeight;
      }, 500);

      // Marca que já processamos este estado inicial
      lastAnimationStateRef.current = currentState;

      return;
    }

    const wasShowingGlobalProgress =
      wrapperRef.current.dataset.showingGlobalProgress === 'true';

    // Se estava e continua mostrando GlobalProgress, não anima altura
    if (wasShowingGlobalProgress && isShowingGlobalProgress) {
      wrapperRef.current.dataset.showingGlobalProgress = 'true';
      lastAnimationStateRef.current = currentState;
      return;
    }

    // Atualiza o estado
    wrapperRef.current.dataset.showingGlobalProgress = isShowingGlobalProgress
      ? 'true'
      : 'false';

    // Determina se wrapper tem altura inline fixada
    const wrapperHasInlineHeight =
      wrapperRef.current.style.height &&
      wrapperRef.current.style.height !== 'auto';

    // Se wrapper perdeu a altura inline mas temos altura armazenada, fixa imediatamente
    if (!wrapperHasInlineHeight && previousContentHeightRef.current !== null) {
      wrapperRef.current.style.height = `${previousContentHeightRef.current}px`;
    }

    // Define oldHeight: usa altura inline do wrapper (mais confiável) ou altura armazenada
    let oldHeight;

    if (wrapperHasInlineHeight) {
      // Wrapper tem altura inline: usa essa altura (é o que está sendo exibido agora)
      oldHeight = wrapperRef.current.getBoundingClientRect().height;
    } else if (previousContentHeightRef.current === null) {
      // Primeira transição sem altura armazenada: não temos oldHeight
      oldHeight = null;
    } else {
      // Wrapper estava em auto mas acabamos de fixar: usa altura armazenada
      oldHeight = previousContentHeightRef.current;
    }

    // Aguarda o React completar o ciclo de renderização para capturar a NOVA altura
    animationTimeoutRef.current = setTimeout(() => {
      if (!wrapperRef.current || !contentRef.current) return;

      // Agora captura a nova altura diretamente do conteúdo (já renderizado pelo React)
      const newHeight = contentRef.current.getBoundingClientRect().height;

      // Se oldHeight é null (primeira transição sem altura armazenada), não anima
      // Apenas fixa o wrapper na altura atual e armazena para próxima transição
      if (oldHeight === null) {
        wrapperRef.current.style.height = `${newHeight}px`;
        previousContentHeightRef.current = newHeight;
        lastAnimationStateRef.current = currentState;
        return;
      }

      const diff = Math.abs(oldHeight - newHeight);

      // Se a altura não mudou significativamente (tolerância de 1px), não anima
      if (diff < 1) {
        lastAnimationStateRef.current = currentState;
        return;
      }

      // Fixa na altura antiga sem transição (se ainda não estiver fixada)
      wrapperRef.current.style.transition = 'none';
      wrapperRef.current.style.height = `${oldHeight}px`;

      // Force reflow
      // void wrapperRef.current.offsetHeight;
      // Garante que o estilo 'height: auto' seja aplicado antes de medir a nova altura
      wrapperRef.current.getBoundingClientRect();

      // Reativa transição e anima para nova altura
      requestAnimationFrame(() => {
        if (wrapperRef.current) {
          wrapperRef.current.style.transition = `height ${
            ANIMATION_DURATIONS.BASE / 1000
          }s ease`;
          wrapperRef.current.style.height = `${newHeight}px`;

          // Armazena a nova altura para usar na PRÓXIMA transição
          previousContentHeightRef.current = newHeight;

          // Marca que animamos para este estado
          lastAnimationStateRef.current = currentState;
        }
      });
    }, ANIMATION_DURATIONS.RENDER_DELAY); // Aguardar React/browser completarem renderização (incluindo API)
  }, [
    displayStatusType,
    displayLocationPathname,
    globalProgressBusy,
    isShowingGlobalProgress,
    status,
  ]);

  /**
   * Aplica animação de altura após medir novo conteúdo no resize
   */
  const applyResizeAnimation = (currentWrapperHeight, newContentHeight) => {
    if (!wrapperRef.current) return;

    wrapperRef.current.style.transition = `height ${
      ANIMATION_DURATIONS.BASE / 1000
    }s ease`;
    wrapperRef.current.style.height = `${newContentHeight}px`;

    // Atualiza altura armazenada
    previousContentHeightRef.current = newContentHeight;

    console.log(
      '[useHeightAnimation] Resize animation applied, new height:',
      newContentHeight,
    );
  };

  /**
   * Processa medições e decide se anima ou apenas atualiza altura
   */
  const processMeasurementsAndAnimate = (currentWrapperHeight) => {
    if (!wrapperRef.current || !contentRef.current) return;

    const newContentHeight =
      contentRef.current.getBoundingClientRect().height;

    console.log('[useHeightAnimation] Resize measurements:', {
      oldHeight: currentWrapperHeight,
      newHeight: newContentHeight,
      diff: Math.abs(currentWrapperHeight - newContentHeight),
    });

    if (headerContentRef?.current) {
      const headerHeight = headerContentRef.current.lastElementChild.clientHeight;
      if(isDesktop){
        wrapperRef.current.style.top = `${headerHeight}px`;
      }

      wrapperRef.current.style.maxHeight = `${window.innerHeight - headerHeight - 15}px`;
    }

    // Se a diferença for significativa (>= 1px), anima
    if (Math.abs(currentWrapperHeight - newContentHeight) >= 1) {
      // Garante o ponto de partida sem transição (o wrapper já está nessa
      // altura; reafirmar evita herdar uma transição pendente).
      wrapperRef.current.style.transition = 'none';
      wrapperRef.current.style.height = `${currentWrapperHeight}px`;

      // Force reflow
      wrapperRef.current.getBoundingClientRect();

      // Anima para nova altura
      requestAnimationFrame(() =>
        applyResizeAnimation(currentWrapperHeight, newContentHeight),
      );
    } else {
      // Diferença insignificante: fixa a altura atual sem animar.
      wrapperRef.current.style.transition = 'none';
      wrapperRef.current.style.height = `${newContentHeight}px`;
      previousContentHeightRef.current = newContentHeight;

      console.log(
        '[useHeightAnimation] Resize: height unchanged, no animation needed',
      );
    }
  };

  /**
   * Executa recálculo de altura após debounce
   */
  const executeResizeRecalculation = () => {
    console.log('[useHeightAnimation] Window resized, recalculating height');

    // Modal aberto (wrapper escalado): não remedir — a medição viria
    // escalada e encolheria o card. Ver isWrapperScaled.
    if (isWrapperScaled()) {
      return;
    }

    // Captura a altura atual do wrapper (antes do resize)
    const currentWrapperHeight =
      wrapperRef.current.getBoundingClientRect().height;

    // A nova altura é medida diretamente no contentRef (filho), que tem
    // altura natural por si só. NÃO tornamos o wrapper height:auto: fazê-lo
    // pintava o card em 100% por um frame antes de voltar e animar — a
    // piscada vista no Safari mobile entre o hide do loading e o início da
    // animação de abertura. Mantendo o wrapper na altura atual, animamos
    // direto da altura antiga para a nova, sem frame intermediário.
    processMeasurementsAndAnimate(currentWrapperHeight);
  };

  /**
   * Handler para resize com debounce
   * Recalcula a altura do wrapper quando a janela muda de tamanho
   * ou quando o dispositivo muda de orientação
   */
  const handleResize = () => {
    // Verifica refs no momento do resize
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }

    // Limpa timeout anterior para implementar debounce
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Aguarda 150ms após o último evento de resize para processar
    resizeTimeoutRef.current = setTimeout(executeResizeRecalculation, 150);
  };

  // Effect para tratar resize da janela e mudanças de orientação
  useLayoutEffect(() => {
    // Adiciona listeners para resize e orientationchange
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    console.log('[useHeightAnimation] Resize listeners attached');

    // Cleanup: remove listeners e cancela timeouts pendentes
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }

      console.log('[useHeightAnimation] Resize listeners removed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vazio: efeito roda apenas na montagem/desmontagem

  return { wrapperRef, contentRef };
};
